import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Table,
  Input,
  Button,
  Spin,
  Popconfirm,
  message,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import PLAN_CATALOG from '../../../constants/plans';
import { displayPlanName } from '../../../utils/planDisplay';
import { adminApi } from '../../../modules/api';
import { clearSession } from '../../../modules/auth';
import { refreshSubscriptions, subscribeToSubscriptionChanges } from '../../../modules/subscriptionStorage';
import './adminCustomers.css';

const SYSTEM_ACCOUNT_EMAIL = 'admin@soalive.net';

class AdminCustomers extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      data: [],
      meta: {
        total: 0,
        page: 1,
        limit: 20,
        pages: 1,
      },
      search: '',
      baseData: [],
      localSubscriptions: refreshSubscriptions(),
    };
  }

  componentDidMount() {
    this.fetchCustomers();
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      this.refreshListener = () => {
        const { meta, search } = this.state;
        this.fetchCustomers(meta.page || 1, search);
      };
      this.storageRefreshListener = (event) => {
        if (event && event.key === 'asi:admin-customers-refresh-ts') {
          this.refreshListener();
        }
      };
      window.addEventListener('asi:admin-customers-refresh', this.refreshListener);
      window.addEventListener('storage', this.storageRefreshListener);
      this.unsubscribeSubscriptions = subscribeToSubscriptionChanges((map) => {
        this.setState(
          {
            localSubscriptions: map,
          },
          () => {
            this.setState((prevState) => ({
              data: this.mergeWithLocal(prevState.baseData || []),
            }));
            if (this.refreshListener) {
              this.refreshListener();
            }
          },
        );
      });
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('asi:admin-customers-refresh', this.refreshListener);
      window.removeEventListener('storage', this.storageRefreshListener);
    }
    if (typeof this.unsubscribeSubscriptions === 'function') {
      this.unsubscribeSubscriptions();
    }
  }

  async fetchCustomers(page = 1, search = this.state.search) {
    this.setState({ loading: true });
    try {
      const response = await adminApi.fetchCustomers({ page, search });
      const payload = response && response.data ? response.data : response;
      const merged = this.mergeWithLocal(payload.data || []);
      this.setState({
        loading: false,
        data: merged,
        baseData: payload.data || [],
        meta: payload.meta || this.state.meta,
        search,
      });
    } catch (error) {
      const fallback = this.mergeWithLocal([]);
      this.setState({
        loading: false,
        data: fallback,
        baseData: [],
      });
    }
  }

  handleSearchSubmit = () => {
    const { search } = this.state;
    this.fetchCustomers(1, search);
  };

  handleDelete = async (record) => {
    try {
      await adminApi.deleteCustomer(record.id);
      message.success('Customer account removed.');
      const { meta, search } = this.state;
      const nextPage = meta.page > 1 && this.state.data.length === 1 ? meta.page - 1 : meta.page;
      this.fetchCustomers(nextPage, search);
    } catch (error) {
      const errorMessage = error && error.response && error.response.data
        && error.response.data.message;
      message.error(errorMessage || 'Unable to remove this customer account.');
    }
  };

  mergeWithLocal = (serverData = []) => {
    const combined = new Map();
    const subscriptionsMap = this.state.localSubscriptions || {};

    serverData.forEach((item) => {
      const key = item && (item.id || item._id);
      if (!key || String(item.email || '').toLowerCase() === SYSTEM_ACCOUNT_EMAIL) {
        return;
      }
      combined.set(String(key), { ...item, id: String(key) });
    });

    Object.entries(subscriptionsMap).forEach(([userId, subscription]) => {
      if (
        !subscription
        || !subscription.planId
        || String(subscription.userEmail || '').toLowerCase() === SYSTEM_ACCOUNT_EMAIL
      ) {
        return;
      }

      const plan = PLAN_CATALOG.find((planItem) => planItem.id === subscription.planId);
      const planPrice =
        subscription.planPriceAmount !== undefined && subscription.planPriceAmount !== null
          ? Number(subscription.planPriceAmount || 0)
          : plan
          ? Number(plan.priceAmount || 0)
          : 0;

      const existing = combined.get(userId) || {
        id: userId,
        email: subscription.userEmail || 'unknown@local.test',
        name: subscription.userName || 'Local user',
        company: subscription.userCompany || '—',
        createdAt: subscription.approvedAt || new Date().toISOString(),
        subscriptions: {
          total: 0,
          active: 0,
          lifetimeValue: 0,
          activeValue: 0,
          activePlanName: null,
          latestEndsAt: null,
        },
      };

      const subscriptions = existing.subscriptions || {};
      const updatedSubscriptions = {
        ...subscriptions,
        total: subscriptions.total && subscriptions.total > 0 ? subscriptions.total : 1,
        active: subscriptions.active && subscriptions.active > 0 ? subscriptions.active : 1,
        lifetimeValue:
          subscriptions.lifetimeValue && subscriptions.lifetimeValue > 0
            ? subscriptions.lifetimeValue
            : planPrice,
        activeValue:
          subscriptions.activeValue && subscriptions.activeValue > 0
            ? subscriptions.activeValue
            : planPrice,
        activePlanName: subscriptions.activePlanName || (plan && plan.name) || subscription.planName || null,
        latestEndsAt: subscriptions.latestEndsAt || subscription.approvedAt || null,
      };

      combined.set(userId, {
        ...existing,
        email: subscription.userEmail || existing.email || 'unknown@local.test',
        name: subscription.userName || existing.name || 'Local user',
        company: subscription.userCompany || existing.company || '—',
        subscriptions: updatedSubscriptions,
      });
    });

    return Array.from(combined.values());
  };

  render() {
    const { loading, data, meta, search } = this.state;
    const { admin, onLogout } = this.props;
    const isViewer = admin && admin.user && admin.user.type === 'viewer';

    const columns = [
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        render: (value, record) => (
          <Link to={`/admin/customers/${record.id}`}>{value}</Link>
        ),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Company',
        dataIndex: 'company',
        key: 'company',
      },
      {
        title: 'Active plan',
        key: 'activePlanName',
        render: (value, record) => {
          const planName = displayPlanName(record.subscriptions && record.subscriptions.activePlanName);
          return planName ? (
            <span className="admin-customers__plan-name" title={planName}>{planName}</span>
          ) : '—';
        },
      },
      {
        title: 'Next renewal',
        key: 'nextRenewal',
        render: (value, record) => {
          const renewal = record.subscriptions ? record.subscriptions.latestEndsAt : null;
          return renewal ? new Date(renewal).toLocaleDateString() : '—';
        },
      },
      {
        title: 'Current credits left',
        key: 'currentCreditsLeft',
        render: (value, record) => {
          const subscription = record && record.subscriptions;
          if (!subscription || !subscription.active) {
            return '—';
          }
          const quotaLimit = subscription.activeQuotaLimit;
          if (quotaLimit == null) {
            return 'Unlimited';
          }
          const quotaRemaining = subscription.activeQuotaRemaining;
          const safeLimit = Number(quotaLimit);
          const safeRemaining = quotaRemaining == null
            ? Math.max(Number(quotaLimit) - Number(subscription.activeQuotaUsed || 0), 0)
            : Number(quotaRemaining);
          if (!Number.isFinite(safeLimit) || !Number.isFinite(safeRemaining)) {
            return '—';
          }
          return `${Math.max(safeRemaining, 0).toLocaleString()} / ${Math.max(safeLimit, 0).toLocaleString()}`;
        },
      },
      ...(!isViewer ? [{
        title: 'Actions',
        key: 'actions',
        render: (value, record) => {
          if (!record.canDelete) {
            const reason = record.deleteBlockedReason === 'credits'
              ? 'Credits remain'
              : 'Usage recorded';
            return <span title={`Cannot remove: ${reason}`}>—</span>;
          }
          return (
            <Popconfirm
              title="Remove this customer account?"
              description="This removes the account and its account records permanently."
              okText="Remove"
              cancelText="Cancel"
              onConfirm={() => this.handleDelete(record)}
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                aria-label={`Remove ${record.email}`}
                title="Remove account"
              />
            </Popconfirm>
          );
        },
      }] : []),
    ];

    return (
      <AdminLayout
        title="Customers"
        subtitle="Manage customer accounts, credit plans, and usage."
        activeKey="customers"
        admin={admin}
        onLogout={onLogout}
      >
        <div className="admin-customers__toolbar">
          <Input
            placeholder="Search by email, name, or company"
            value={search}
            onChange={(event) => this.setState({ search: event.target.value })}
            onPressEnter={this.handleSearchSubmit}
            allowClear
          />
          <Button type="primary" onClick={this.handleSearchSubmit}>Search</Button>
        </div>
        {loading ? (
          <div className="admin-customers__loading">
            <Spin />
          </div>
        ) : (
          <Table
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={data}
            pagination={{
              current: meta.page,
              total: meta.total,
              pageSize: meta.limit,
              onChange: (pageNumber) => this.fetchCustomers(pageNumber),
            }}
          />
        )}
      </AdminLayout>
    );
  }
}

AdminCustomers.propTypes = {
  admin: PropTypes.shape({
    user: PropTypes.object,
  }),
  onLogout: PropTypes.func.isRequired,
};

AdminCustomers.defaultProps = {
  admin: null,
};

const mapStateToProps = (state) => ({
  admin: state.auth.admin,
});

const mapDispatchToProps = {
  onLogout: clearSession,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminCustomers);
